import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Zap, Radio, Activity, Send, FileText,
  Shield, Users, Cpu, ChevronLeft, LogOut, ArrowLeftRight, Layers, CandlestickChart, CalendarDays, ScrollText,
  Link, CalendarCheck, CreditCard, Package, Wallet, Globe, Receipt, Briefcase, Star, Palette,
} from 'lucide-react';
import { useUIStore } from '../../store';
import { useSessionStore } from '../../session/store';
import { useBranding } from '../../branding/portalBrandingState';
import { LOGO_FALLBACK, resolveSidebarBrand } from '../../branding/brandingPresentation';
import BrandLogoImage from '../../branding/BrandLogoImage';
import { USER_NAV, OPERATOR_NAV, ADMIN_NAV, OPERATOR_ADMIN_NAV } from '../../routes/constants';
import { cn } from '../../lib/utils';
import { isEmbedded } from '../../app/config';

const ICON_MAP = { LayoutDashboard, Zap, Radio, Activity, Send, FileText, Shield, Users, Cpu, Layers, CandlestickChart, CalendarDays, ScrollText, Link, CalendarCheck, CreditCard, Package, Wallet, Globe, Receipt, Briefcase, Star, Palette };

function NavItem({ to, label, icon, collapsed }) {
  const Icon = ICON_MAP[icon] || Activity;
  return (
    <NavLink to={to} className={({ isActive }) => cn(
      'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-150 group relative',
      isActive
        ? 'bg-amber/10 text-amber font-medium sidebar-nav-active'
        : 'text-txt-secondary hover:bg-bg-hover hover:text-txt-primary font-normal'
    )}>
      {({ isActive }) => (
        <>
          {isActive && <span className="side-tick absolute left-0 inset-y-1.5 w-0.5 rounded-r-full bg-amber" />}
          <Icon size={15} className="shrink-0" />
          {!collapsed && <span className="truncate">{label}</span>}
          {collapsed && (
            <div className="absolute left-full ml-2.5 px-2.5 py-1.5 bg-bg-card border border-bg-border rounded-lg text-xs text-txt-primary whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity shadow-panel">
              {label}
            </div>
          )}
        </>
      )}
    </NavLink>
  );
}

function WorkspaceSwitcher({ workspace, setWorkspace, canAdmin, collapsed, role }) {
  if (!canAdmin) return null;
  const isAdmin = workspace === 'admin';
  const adminLabel = role === 'operator' ? 'Operator Workspace' : 'Admin Workspace';
  return (
    <button
      onClick={() => setWorkspace(isAdmin ? 'user' : 'admin')}
      className={cn(
        'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs border transition-all duration-150',
        isAdmin
          ? 'bg-amber/15 text-amber border-amber/40 hover:bg-amber/15'
          : 'bg-bg-hover text-txt-secondary border-bg-border hover:text-txt-primary hover:border-bg-hover'
      )}
    >
      <ArrowLeftRight size={11} className="shrink-0" />
      {!collapsed && (
        <span>{isAdmin ? adminLabel : 'User Workspace'}</span>
      )}
    </button>
  );
}

const SIDEBAR_CLOCK_DATE_FMT = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata',
});
const SIDEBAR_CLOCK_TIME_FMT = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
});

function SidebarClock({ collapsed }) {
  const [t, setT] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  if (collapsed) return null;
  return (
    <div className="px-3 py-1.5 text-2xs text-txt-muted font-mono tracking-wide">
      {SIDEBAR_CLOCK_DATE_FMT.format(t)} · {SIDEBAR_CLOCK_TIME_FMT.format(t)} IST
    </div>
  );
}

export default function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, theme } = useUIStore();
  const { workspace, setWorkspace, clearSession, user, canAdmin } = useSessionStore();
  const role = useSessionStore((s) => s.role);
  const isSuperAdmin = useSessionStore((s) => s.isSuperAdmin);
  const brand = resolveSidebarBrand(useBranding());

  if (isEmbedded && sidebarCollapsed) return null;

  const nav = workspace === 'admin'
    ? (role === 'operator' ? OPERATOR_ADMIN_NAV : ADMIN_NAV)
    : USER_NAV;

  return (
    <aside className={cn(
      'flex flex-col bg-bg-surface border-r border-bg-border transition-all duration-200 shrink-0',
      sidebarCollapsed ? 'w-14' : 'w-52'
    )}>
      {/* Logo */}
      <div className="flex items-center justify-between gap-2 px-3 py-4 border-b border-bg-border min-h-[56px]">
        {!sidebarCollapsed && (
          <div className="min-w-0 flex-1">
            {(brand.logoSrc || brand.logoFallback !== LOGO_FALLBACK.NONE) && (
              <div className={cn(
                'inline-block max-w-full',
                // The dark chip exists only for the bundled asset, whose light
                // variant is still missing; a custom Operator logo needs no chip.
                theme === 'light' && !brand.logoSrc && brand.logoFallback === LOGO_FALLBACK.BUNDLED
                  && 'bg-[#0B0E14] rounded-lg px-3 py-1.5 border border-black/20 shadow-card'
              )}>
                {/* The main AlgoSys host keeps the bundled AlgoSys logo. Any
                    other host shows the Operator's loaded logo or a neutral
                    placeholder while loading, never the AlgoSys logo. A branded
                    portal states its identity in the separate line below. */}
                <BrandLogoImage
                  logoSrc={brand.logoSrc}
                  alt={brand.logoAlt}
                  fallback={brand.logoFallback}
                  className="h-16 object-contain"
                />
              </div>
            )}
            {brand.showBusinessName && (
              <p
                className="mt-1 truncate text-xs font-semibold text-brand"
                title={brand.businessName}
              >
                {brand.businessName}
              </p>
            )}
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="p-1.5 rounded-lg hover:bg-bg-hover text-txt-muted hover:text-txt-secondary transition-colors ml-auto"
        >
          <ChevronLeft size={13} className={cn('transition-transform duration-200', sidebarCollapsed && 'rotate-180')} />
        </button>
      </div>

      {/* Workspace switcher */}
      {canAdmin() && (
        <div className="px-2 pt-2.5">
          <WorkspaceSwitcher
            workspace={workspace}
            setWorkspace={setWorkspace}
            canAdmin={canAdmin()}
            collapsed={sidebarCollapsed}
            role={role}
          />
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto no-scrollbar mt-1">
        {!sidebarCollapsed && (
          <div className="side-section px-3 py-1.5 mb-1 text-2xs text-txt-label tracking-widest uppercase">
            {workspace === 'admin' ? (role === 'operator' ? 'Operator' : 'Admin') : 'Workspace'}
          </div>
        )}
        {nav.filter((item) => !item.superAdminOnly || isSuperAdmin).map((item) => (
          <NavItem key={item.to} {...item} collapsed={sidebarCollapsed} />
        ))}
      </nav>

      {/* Footer */}
      <div className="p-2.5 border-t border-bg-border space-y-1">
        <SidebarClock collapsed={sidebarCollapsed} />
        <button
          onClick={clearSession}
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm w-full text-txt-muted hover:bg-bear/10 hover:text-bear transition-all duration-150 border border-transparent"
        >
          <LogOut size={13} className="shrink-0" />
          {!sidebarCollapsed && <span className="font-medium text-xs">Logout</span>}
        </button>
      </div>
    </aside>
  );
}
